import './AddToWishlist.scss';

import { Field, Form, Formik } from 'formik';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ButtonLoading from '@/common/components/molecules/ButtonLoading/ButtonLoading';
import SelectField from '@/common/components/molecules/SelectField/SelectField';
import TextField from '@/common/components/molecules/TextField/TextField';
import { useModalContext } from '@/common/contexts/ModalContext';
import { useTagManager } from '@/common/contexts/TagManagerContext';
import { useWishlistsContext } from '@/common/contexts/WishlistsContext';
import useWishlist from '@/common/hooks/wishlists/useWishlist';
import SelectGroup from '@/ebat/components/blocks/Forms/SelectGroup/SelectGroup';
import useWishlists from '@/ebat/components/pages/Wishlists/useWishlists';
import { TAG_MANAGER } from '@/ebat/constants';
import useWishlistsTree from '@/ebat/hooks/wishlists/useWishlistsTree';

import {
  AddToWishlistDefaultProps,
  AddToWishlistPropTypes,
} from './AddToWishlist.proptypes';

const AddToWishlist = ({
  disabled,
  isLoading,
  items,
  product,
  reference,
  setFavorite,
}) => {
  const { t } = useTranslation();
  const { onCloseModal } = useModalContext();
  const { onPushEvent } = useTagManager();

  const {
    isDisabled: isDisabledUseWishlist,
    onAddProductsToWishlist,
  } = useWishlist({ disabled, isLoading });

  const {
    data: wishlistsTree,
    isLoading: isLoadingWishlistsTree,
  } = useWishlistsTree(product);

  const {
    isDisabled: isDisabledUseWishlists,
    sortWishlistsData,
  } = useWishlists({
    isLoading: isLoadingWishlistsTree,
    tree: wishlistsTree,
  });

  const { onAddWishlist, isLoading: isLoadingWishlist } = useWishlistsContext();

  /**
   * Add products to wishlist using sku
   * @param {object} wishlist wishlist concerned by the addition of products
   * @type {(function(object): void)|*}
   */
  const addProductsToWishlist = useCallback(
    (wishlist) => {
      onAddProductsToWishlist({
        wishlist,
        items,
        onSuccessCallback: () => {
          setFavorite(true);
          onCloseModal();
        },
      });
    },
    [onAddProductsToWishlist, items, setFavorite, onCloseModal],
  );

  /**
   * Push dataLayer
   * @type {function(): void}
   */
  const handlePushDataLayer = useCallback(
    () => onPushEvent({ event: TAG_MANAGER.EVENTS.CTA_ADD_WISHLIST }),
    [onPushEvent],
  );

  /**
   * Actions on add wishlist success
   * @type {(...args: any[]) => any}
   * @param newWishlist {object}
   */
  const handleAddWishlistSuccess = useCallback(
    (newWishlist) => {
      addProductsToWishlist(newWishlist);
      setFavorite();
      handlePushDataLayer();
    },
    [addProductsToWishlist, handlePushDataLayer, setFavorite],
  );

  /**
   * Add a new wishlist to the parent category and onSuccess adds the products to this wishlist
   * @param {object} wishlist new wishlist
   * @type {(function(object): void)|*}
   */
  const addNewWishlist = useCallback(
    (wishlist) => {
      onAddWishlist({
        name: wishlist.name,
        categoryId: wishlist.category_id,
        onSuccess: handleAddWishlistSuccess,
      });
    },
    [handleAddWishlistSuccess, onAddWishlist],
  );

  /**
   * Check for disabled
   * @type {boolean}
   */
  const isDisabled = useMemo(() => {
    const loading =
      isLoading ||
      isLoadingWishlist ||
      isLoadingWishlistsTree ||
      isDisabledUseWishlist.loading ||
      isDisabledUseWishlists.loading;

    return {
      disabled:
        loading ||
        disabled ||
        isDisabledUseWishlist.disabled ||
        isDisabledUseWishlists.disabled,
      loading,
    };
  }, [
    disabled,
    isDisabledUseWishlist,
    isDisabledUseWishlists,
    isLoading,
    isLoadingWishlist,
    isLoadingWishlistsTree,
  ]);

  return (
    <div
      className="modal-add-to-wishlist__content"
      data-qa-id="modal-add-to-wishlist"
    >
      <div className="action-container">
        <div className="existing-list">
          <h2 className="rs-modal-title">
            {t('common:messages:actions.wishlist.select')}
          </h2>
          <Formik
            initialValues={{
              name: reference,
              id: null,
            }}
            onSubmit={async (values) => addProductsToWishlist(values)}
          >
            {({ errors, touched }) => (
              <Form
                className="form modal-form"
                data-qa-id="select-wishlist-form"
              >
                <Field
                  as={SelectGroup}
                  context="id-field"
                  id="id"
                  name="id"
                  placeholder={t(
                    'common:messages:actions.wishlist.select_list',
                  )}
                  options={sortWishlistsData}
                  disabled={isDisabled.disabled}
                  required
                  errors={
                    errors.id &&
                    touched.id && [
                      t(`common:messages:forms.validation.${errors.id}`),
                    ]
                  }
                >
                  {t('common:messages:actions.wishlist.existing')}
                </Field>
                <ButtonLoading
                  context="select-wishlist-save"
                  disabled={isDisabled.disabled}
                  isLoading={isDisabled.loading}
                >
                  {t('common:messages:actions.wishlist.add_list')}
                </ButtonLoading>
              </Form>
            )}
          </Formik>
        </div>
        <div className="new-list">
          <h2 className="rs-modal-title">
            {t('common:messages:actions.wishlist.create_new_list')}
          </h2>
          <Formik
            initialValues={{
              name: reference,
              category_id: null,
            }}
            onSubmit={async (values) => addNewWishlist(values)}
          >
            {({ errors, touched }) => (
              <Form
                className="form modal-form"
                data-qa-id="add-to-wishlist-form"
              >
                <Field
                  as={SelectField}
                  context="category-id-field"
                  id="category_id"
                  name="category_id"
                  placeholder={t(
                    'common:messages:actions.wishlist.select_file',
                  )}
                  options={sortWishlistsData?.categories?.map(
                    ({ id: value, name: key }) => ({
                      key,
                      value: value.toString(),
                    }),
                  )}
                  disabled={isDisabled.disabled}
                  errors={
                    errors.category_id &&
                    touched.category_id && [
                      t(
                        `common:messages:forms.validation.${errors.category_id}`,
                      ),
                    ]
                  }
                >
                  {t('common:messages:actions.wishlist.files')}
                </Field>
                <Field
                  as={TextField}
                  context="name-field"
                  id="name"
                  name="name"
                  className="new-wishlist-name"
                  disabled={isDisabled.disabled}
                  required
                  errors={
                    errors.name &&
                    touched.name && [
                      t(`common:messages:forms.validation.${errors.name}`),
                    ]
                  }
                >
                  {t('common:messages:actions.wishlist.entitled')}
                </Field>
                <ButtonLoading
                  context="add-wishlist-save"
                  disabled={isDisabled.disabled}
                  isLoading={isDisabled.loading}
                >
                  {t('common:messages:actions.wishlist.save')}
                </ButtonLoading>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

AddToWishlist.defaultProps = {
  ...AddToWishlistDefaultProps,
};

AddToWishlist.propTypes = {
  ...AddToWishlistPropTypes,
};

export default AddToWishlist;
